/**
 * `common.ts` has been extracted from `validation/index.ts` to improve
 * tree-shaking and reduce bundle size for consumers.
 *
 * In addition, note that the validation module (`validation/index.ts)`
 * uses export default with an anonymous object, which gets transpiled
 * to `exports.default` in (CommonJS). This can cause compatibility
 * issues with projects expecting ES module syntax (such as `instore`).
 * To address this, `common.ts` offers named exports as an alternative.
 */

import isMobilePhone from 'validator/lib/isMobilePhone';
import latinize from './latinize';
import { isDefined } from '../legos/utils/tsutils';

const phonePattern = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/;
export const namePattern = /^[a-zA-Z]+(([\s,'-.][a-zA-Z ])?[a-zA-Z]*)*$/;

/**
 * @param {string} phone - in format (XXX) xxx-xxxx
 * @return boolean
 */
const isValidPhone = (phone: string): boolean => {
  // i.e. area code of 555, 777 etc. are not valid.
  const repeatedNumbersPattern = /^(.)\1*$/;

  if (phone.length !== 14) return false;

  const onlyNumbers = phone.replace(/\D/g, '');

  // phone is all the same number i.e. 777-777-7777
  return !repeatedNumbersPattern.test(onlyNumbers);
};

export const isPhone = (phone: string | undefined | null): phone is string =>
  phone != null &&
  phonePattern.test(phone) &&
  isMobilePhone(phone.replace(/\D/g, ''), 'en-US') &&
  isValidPhone(phone);

export const isName = (
  name: string | undefined | null,
  minLength = 2,
  maxLength = 24,
): name is string => {
  if (!isDefined(name)) return false;

  const trimmedName = name.trim();

  return (
    namePattern.test(latinize(trimmedName)) &&
    trimmedName.length >= minLength &&
    trimmedName.length <= maxLength
  );
};

export const isMiddleName = (name: string | undefined | null): name is string =>
  isName(name, 1, 24);
export const isLongName = (name: string | undefined | null): name is string =>
  isName(name, 2, 30);
